// The story's screens on @rpg/ui (gate D1.3, decision 0046): the dialogue box (the speaker, the line, and the choices or
// a continue button; keyboard and pad move between them and press, touch taps; every text from the strings table by id)
// and the quest log (active and finished quests, their objectives, tracking: @rpg-unit/quest-journal's screen), opened
// from the pause screen. A notice strip (@rpg-unit/quest-journal's, fed by its module's story:quest) says when a quest
// is offered, an objective is done or a quest is finished. The dialogue box shows while a conversation is open and
// not waiting on a scene, when no other screen is up.
import { Button, h, Menu, Screen, type ScreenContext, type ScreenRender, type Ui } from "@rpg/ui";
import { journalScreen, mountQuestNotice, type JournalLabels } from "@rpg-unit/quest-journal";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { StoryMount } from "./index";

const CSS = `
.rpg-layer:has(> [data-screen=dialogue]){align-items:flex-end;padding-bottom:max(18px,env(safe-area-inset-bottom))}
.rpg-screen[data-screen=dialogue]{width:min(92vw,680px);max-height:46vh}
.rpg-screen[data-screen=dialogue] .gv-line{font-size:1.15em;line-height:1.45;margin:.2em 0 .8em}
`;

/** This game's string ids for the quest journal's lines and notices. */
const LABELS: JournalLabels = {
  title: "story.log.title",
  open: "story.log.open",
  tabs: "story.log.tabs",
  active: "story.log.active",
  done: "story.log.done",
  none: "story.log.none",
  main: "story.log.main",
  side: "story.log.side",
  giver: "story.log.giver",
  tracked: "story.log.tracked",
  failed: "story.log.failed",
  track: "story.log.track",
  objectiveDone: "story.log.objective.done",
  objectiveCurrent: "story.log.objective.current",
  count: "story.log.count",
  noticeObjective: "story.notice.objective",
  noticeQuest: "story.notice.quest",
  noticeDone: "story.notice.done",
  noticeFailed: "story.notice.failed",
};

/** Mount the dialogue box, the quest log and the notice. */
export function mountStoryUi(o: { ui: Ui; addScreens(extra: Record<string, ScreenRender>, pause?: { label: string; action: string; screen: string }[]): void; content: SpikeContent; runtime: Runtime; story: StoryMount; playing(): boolean; pause?: () => void }) {
  if (!document.getElementById("gv-story-css")) {
    const s = document.createElement("style");
    s.id = "gv-story-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const t = o.content.t;
  const st = o.story;
  /** The last line said in the conversation open (shown with the choices after it). */
  let lastLine: string | null = null;

  const dialogue: ScreenRender = (ctx: ScreenContext) => {
    const v = st.view();
    if (!v) return h(Screen, { ctx, title: "", showBack: false, onBack: () => {} });
    const name = t(v.name);
    // back (Esc, the pad's B) pauses: pause anywhere, in a conversation too (phase D joined, decision 0063; agent call)
    return h(
      Screen,
      { ctx, title: name, showBack: false, onBack: () => o.pause?.() },
      // the last line stays above the choices it leads to
      (v.line ?? lastLine) ? h("p", { class: "gv-line", "data-line": v.line ?? lastLine }, t((v.line ?? lastLine)!)) : null,
      v.choices.length
        ? h(Menu, { label: t("story.dialogue.label", { speaker: name }) }, ...v.choices.map((id, i) => h(Button, { label: t(id), action: `choice-${i}`, autofocus: i === 0, onPress: () => st.handle.choose(i) })))
        : h(Menu, null, h(Button, { label: t("story.dialogue.continue"), variant: "primary", action: "continue", autofocus: true, onPress: () => st.handle.advance() })),
    );
  };

  // the quest log is @rpg-unit/quest-journal's, said in this game's strings
  const quests: ScreenRender = journalScreen({ journal: () => st.handle.journal(), track: (q) => st.handle.track(q), npcName: (n) => st.npcName(n), t, labels: LABELS });
  o.addScreens({ dialogue, quests }, [{ label: LABELS.open, action: "quests", screen: "quests" }]);

  // the notice strip (@rpg-unit/quest-journal), fed each quest change by the journal's module
  const notice = mountQuestNotice({
    title: (q) => st.layer().story.quests.get(q)?.title ?? null,
    entry: (q) => st.layer().story.journal().find((x) => x.quest === q),
    t,
    now: () => o.runtime.clock.time,
    labels: LABELS,
  });
  st.journal.onChange((q) => notice.change(q));

  // the dialogue box follows the conversation; a new beat redraws it
  let key = "";
  o.runtime.on(
    "late",
    () => {
      notice.update();
      const v = st.view();
      const want = !!v && !v.waiting && o.playing();
      const top = o.ui.top();
      if (want && top === null) o.ui.push("dialogue");
      else if (!want && top === "dialogue") o.ui.pop();
      if (!v) lastLine = null;
      else if (v.line) lastLine = v.line;
      const k = v ? `${v.npc}|${v.lines}|${v.line ?? ""}|${v.choices.join(",")}` : "";
      if (k !== key) {
        key = k;
        // a new beat is a fresh screen (its first item takes focus: the continue button or the first choice)
        if (o.ui.top() === "dialogue") o.ui.replace("dialogue");
      }
    },
    { order: 55, name: "story: dialogue box" },
  );
  return {
    /** What the notice says (null when hidden). */
    notice: (): string | null => notice.text(),
    openLog: (): void => o.ui.push("quests"),
  };
}
